import {
    DataGrid,
    GridColDef,
    GridRowId,
    GridRowsProp,
    GridToolbarExport,
    GridToolbarQuickFilter,
    GridCellParams,
    GridColumnHeaderParams,
} from "@mui/x-data-grid";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { SoecoButton } from "../../components/SoecoButton";
import { Inventory, MergedInventory } from "../../types";
import { ArrowLeft, ArrowRight, Edit3, Rows } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Pagination, PaginationItem, Stack, Box } from "@mui/material";

type Props = {
    inventories: Inventory[];
    companyId: string;
    mergedInventories?: MergedInventory[];
};

const CustomToolbar = ({
    companyId,
    mergedInventories,
    showMerged,
    onToggleMerged,
}: {
    companyId: string;
    mergedInventories: MergedInventory[];
    showMerged: boolean;
    onToggleMerged: () => void;
}) => {
    const navigate = useNavigate();

    const handleMergeClick = () => {
        navigate(`/companies/${companyId}/merge-inventories`);
    };

    return (
        <Box sx={sx}>
            <Box sx={{ display: "flex", alignItems: "center" }}>
                <GridToolbarExport />
                {/* <SoecoButton
                    size="medium"
                    color={"inherit"}
                    onClick={onToggleMerged}
                    disabled={!mergedInventories || mergedInventories.length < 1}
                >
                    {showMerged ? "Inventories" : "Shared inventories"}
                </SoecoButton> */}
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", marginLeft: 2 }}>
                <GridToolbarQuickFilter
                    debounceMs={500}
                    quickFilterParser={value => value.split(",").map(v => v.trim())}
                    placeholder="Search"
                    variant="outlined"
                />
            </Box>
        </Box>
    );
};

export const InventoryList = ({ inventories, companyId, mergedInventories }: Props) => {
    const navigate = useNavigate();
    const [page, setPage] = useState(1);
    const [pageSize] = useState(100);
    const [showMerged, setShowMerged] = useState(false);

    const handleOnRowClick = (id: GridRowId) => {
        if (showMerged) {
            navigate(`/companies/${companyId}/inventories/${id}`, {
                state: { isMerged: true },
            });
        } else {
            navigate(`/companies/${companyId}/inventories/${id}`, {
                state: { isMerged: false },
            });
        }
    };
    const renderColumnHeader = (params: GridColumnHeaderParams) => {
        const headerName = params.colDef.headerName || params.field;
        const headerRef = useRef<HTMLElement>(null);

        useEffect(() => {
            // Find the parent element with role="columnheader" and add aria-label
            if (headerRef.current) {
                const columnHeaderEl = headerRef.current.closest('[role="columnheader"]');
                if (columnHeaderEl) {
                    columnHeaderEl.setAttribute("aria-label", headerName);

                    // Find the menu button and enhance its accessibility
                    const menuButton = columnHeaderEl.querySelector(
                        'button[aria-label="Menu"]',
                    ) as HTMLButtonElement;

                    if (menuButton) {
                        // Replace generic "Menu" with descriptive label
                        menuButton.setAttribute("aria-label", `Column options for ${headerName}`);

                        // Ensure menu button is in tab order
                        if (menuButton.tabIndex === -1) {
                            menuButton.tabIndex = 0;
                        }
                    }
                }
            }
        }, [headerName]);

        return (
            <strong
                ref={headerRef}
                tabIndex={0}
                style={{ fontWeight: "bold", outline: "none" }}
                onFocus={e => {
                    // Visual focus indicator for keyboard navigation
                    e.currentTarget.style.outline = "2px solid #d4a418";
                    e.currentTarget.style.outlineOffset = "2px";
                }}
                onBlur={e => {
                    // Remove outline when focus leaves
                    e.currentTarget.style.outline = "none";
                }}
            >
                {headerName}
            </strong>
        );
    };
    const handleInventoryEdit = (id: GridRowId) => {
        navigate(`/companies/${companyId}/editInventories/${id}`);
    };

    const baseColumns: GridColDef[] = [
        {
            field: "name",
            headerName: "Name",
            flex: 0.5,
            renderHeader: renderColumnHeader,
            renderCell: params => (
                <button
                    onClick={() => handleOnRowClick(params.id)}
                    onKeyDown={e => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            handleOnRowClick(params.id);
                        }
                    }}
                    style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        font: "inherit",
                        color: "inherit",
                        cursor: "pointer",
                        textAlign: "left",
                        width: "100%",
                    }}
                    aria-label={`Open ${
                        typeof params.value === "string" ? params.value : "inventory"
                    }`}
                >
                    {params.value}
                </button>
            ),
        },
        { field: "project", headerName: "Project", flex: 0.2, renderHeader: renderColumnHeader },
        {
            field: "date",
            headerName: "Date",
            flex: 0.3,
            type: "date",
            renderHeader: renderColumnHeader,
        },
        {
            field: "actions",
            headerName: "Action",
            align: "left",
            flex: 0.2,
            type: "actions",
            renderHeader: renderColumnHeader,
            renderCell: params => {
                return (
                    <TableActions>
                        <SoecoButton
                            icon={true}
                            size="small"
                            onClick={() => handleInventoryEdit(params.id)}
                            aria-label={`inventory, ${params.field}`}
                        >
                            <Edit3 size={24} aria-hidden="true" />
                        </SoecoButton>
                    </TableActions>
                );
            },
        },
    ];

    const columns = showMerged ? baseColumns.filter(col => col.field !== "actions") : baseColumns;

    const activeList = showMerged ? mergedInventories ?? [] : inventories;

    const rows: GridRowsProp = [
        ...activeList.map(inventory => ({
            id: inventory.id,
            name: inventory.name,
            project: inventory.project,
            date: inventory.estimatedDeliveryDate?.slice(0, 10),
        })),
        // Lägg bara till "Merged inventory" om showMerged är false
        ...(showMerged
            ? []
            : [
                  {
                      id: "f4c7c6b0-3c4b-4c3b-9c9b-0b0b0b0b0b0b",
                      name: "Merged inventory",
                      project: "",
                      date: new Date().toISOString().slice(0, 10),
                      sx: {
                          "&:hover": {
                              cursor: "pointer",
                          },
                          color: "red",
                      },
                  },
              ]),
    ];

    const numberOfPages = Math.ceil(rows.length / pageSize);
    const handlePageChange = (_: unknown, newPage: number) => setPage(newPage);

    // Handle Enter key press on table rows for keyboard accessibility (WCAG 2.1.1, 2.4.3, 4.1.2)
    // The name column has its own button handler, so we skip it here
    const handleCellKeyDown = (params: GridCellParams, event: React.KeyboardEvent) => {
        if (
            (event.key === "Enter" || event.key === " ") &&
            params.field !== "actions" &&
            params.field !== "name"
        ) {
            event.preventDefault();
            handleOnRowClick(params.id);
        }
    };

    return (
        <InventoryListWrapper>
            <DataGrid
                disableColumnFilter
                disableColumnSelector
                disableDensitySelector
                rows={rows}
                columns={columns}
                pageSize={pageSize}
                page={page - 1}
                rowsPerPageOptions={[100]}
                hideFooter
                onRowClick={params => handleOnRowClick(params.id)}
                onCellKeyDown={handleCellKeyDown}
                initialState={{
                    sorting: { sortModel: [{ field: "date", sort: "desc" }] },
                }}
                components={{ Toolbar: CustomToolbar }}
                componentsProps={{
                    toolbar: {
                        companyId,
                        mergedInventories: mergedInventories ?? [],
                        showMerged,
                        onToggleMerged: () => setShowMerged(prev => !prev),
                    },
                }}
            />
            <Stack
                spacing={2}
                direction="row"
                justifyContent="space-evenly"
                alignItems="center"
                marginTop={2}
            >
                <Pagination
                    shape="rounded"
                    aria-label="Pagination page navigation"
                    color="primary"
                    page={page}
                    // getItemAriaLabel={(type, page) => {
                    //     let ariaLabel = "";
                    //     if (type === "previous") {
                    //         ariaLabel = "Go to previous page";
                    //     } else if (type === "next") {
                    //         ariaLabel = "Go to next page";
                    //     } else if (type === "page") {
                    //         ariaLabel = page ? `Page ${page}, current page` : `Go to page ${page}`;
                    //     } else if (type === "first") {
                    //         ariaLabel = "Go to first page";
                    //     } else if (type === "last") {
                    //         ariaLabel = "Go to last page";
                    //     } else if (type === "start-ellipsis" || type === "end-ellipsis") {
                    //         ariaLabel = "More pages";
                    //     }

                    //     return ariaLabel;
                    // }}
                    onChange={handlePageChange}
                    count={numberOfPages}
                    // aria-label="Pagination navigation"
                    renderItem={item => {
                        // Generate clear, concise aria-label for each pagination item (WCAG 2.4.6, 4.1.2, 2.4.4)
                        // let ariaLabel = "";
                        // if (item.type === "previous") {
                        //     ariaLabel = "Go to previous page";
                        // } else if (item.type === "next") {
                        //     ariaLabel = "Go to next page";
                        // } else if (item.type === "page") {
                        //     ariaLabel = item.selected
                        //         ? `Page ${item.page || ""}, current page`
                        //         : `Go to page ${item.page || ""}`;
                        // } else if (item.type === "first") {
                        //     ariaLabel = "Go to first page";
                        // } else if (item.type === "last") {
                        //     ariaLabel = "Go to last page";
                        // } else if (item.type === "start-ellipsis" || item.type === "end-ellipsis") {
                        //     ariaLabel = "More pages";
                        // }

                        return (
                            <PaginationItem
                                {...item}
                                aria-label={`Current page is ${item.page || ""}`}
                                // aria-current={item.selected ? "page" : undefined}
                                components={{
                                    previous: () => (
                                        <ArrowLeft
                                            style={{ cursor: "pointer" }}
                                            size={16}
                                            aria-hidden="true"
                                        />
                                    ),
                                    next: () => (
                                        <ArrowRight
                                            style={{ cursor: "pointer" }}
                                            size={16}
                                            aria-hidden="true"
                                        />
                                    ),
                                }}
                                sx={{
                                    backgroundColor: item.selected ? "black" : "transparent",
                                    color: item.selected ? "white" : "black",
                                    "&:hover": {
                                        backgroundColor: item.selected ? "black" : "lightgrey",
                                    },
                                    fontFamily: "'Yllw Sans a1 241219', sans-serif",
                                }}
                            />
                        );
                    }}
                />
            </Stack>
        </InventoryListWrapper>
    );
};

const InventoryListWrapper = styled.div`
    width: 100%;

    /* Make name column buttons keyboard accessible with clear focus indicators (WCAG 2.4.7) */
    .MuiDataGrid-cell button {
        &:hover {
            text-decoration: underline;
        }

        &:focus {
            outline: 2px solid #50724dff;
            outline-offset: 2px;
            border-radius: 2px;
        }

        &:focus-visible {
            outline: 2px solid #50724dff;
            outline-offset: 2px;
        }
    }

    /* Ensure proper cursor for clickable rows */
    .MuiDataGrid-row {
        cursor: pointer;
    }
`;

const TableActions = styled.div`
    display: flex;
    font-size: 6px;
`;

const sx = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
    padding: "8px 16px",
    flexWrap: "nowrap",
    "& .MuiButton-root": {
        backgroundColor: "white",
        color: "#8c8c8c",
        border: "1px solid #9e9e9e",
        marginLeft: "12px",
        fontFamily: "'Inter', sans-serif",
        fontWeight: "600",
        height: "42px",
        minHeight: "42px",
        padding: "0 12px",
        fontSize: "14px",
        textTransform: "none",
    },
    "& .MuiTextField-root": {
        borderRadius: "20px",
        "& .MuiInputBase-root": {
            borderRadius: "50px",
            color: "black",
            border: "1px solid #9e9e9e",
            fontFamily: "'Yllw Sans a1 241219', sans-serif",
            fontWeight: "400",
            height: "42px",
            minHeight: "42px",
            padding: "0 8px",
            fontSize: "14px",
        },
    },
};
